import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { scrollToId } from "../config";
import { MagneticButton } from "./MagneticButton";

const EASE = [0.16, 1, 0.3, 1];
const BRASS =
  "https://images.unsplash.com/photo-1680849112206-a0a04fdc168d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBicmFzcyUyMG1ldGFsJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg3NjY1OTA2fDA&ixlib=rb-4.1.0&q=85";

export const Pricing = () => {
  const { lang, t } = useLanguage();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-[#0D0D0D] py-28 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-[#C5A059]" />
            <span className="font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-[#C5A059]">
              {t.pricing.kicker}
            </span>
          </div>
          <h2 className="font-bebas text-4xl uppercase leading-none tracking-tight text-[#F5F5F5] sm:text-5xl lg:text-6xl">
            {t.pricing.heading}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: EASE }}
          className="group relative mx-auto max-w-3xl"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-[#E65100]/20 to-[#C5A059]/20 opacity-60 blur-2xl transition-opacity duration-700 group-hover:opacity-100" />
          <div
            data-testid="pricing-card"
            className="relative overflow-hidden border border-[#C5A059]/50 bg-[#111111] p-10 md:p-14"
          >
            <img
              src={BRASS}
              alt=""
              loading="lazy"
              className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full object-cover opacity-15 blur-[2px]"
            />
            <span
              data-testid="pricing-trial-badge"
              className="inline-block border border-[#C5A059] bg-[#C5A059]/10 px-4 py-1.5 font-bebas text-sm tracking-[0.2em] text-[#C5A059]"
            >
              {t.pricing.badge}
            </span>

            <div className="mt-10 flex items-end gap-3">
              <span data-testid="pricing-price" className="font-bebas text-8xl leading-none text-[#F5F5F5] md:text-9xl">
                {t.pricing.price}
              </span>
              <span className="pb-3 font-cormorant text-2xl italic text-[#8A9199] md:pb-4 md:text-3xl">
                {t.pricing.per}
              </span>
            </div>

            <p className="mt-4 font-manrope text-sm font-semibold uppercase tracking-[0.14em] text-[#C5A059]">
              {t.pricing.terms}
            </p>

            <ul className="mt-10 space-y-4 border-t border-[#2A2A2A] pt-10">
              {t.pricing.list.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A059]" />
                  <span className="font-manrope text-sm leading-relaxed text-[#8A9199] md:text-base">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-12">
              <MagneticButton>
                <button
                  data-testid="pricing-trial-cta"
                  onClick={() => scrollToId("#contatto")}
                  className="group/btn inline-flex items-center gap-3 bg-[#C5A059] px-8 py-4 font-manrope text-sm font-bold uppercase tracking-[0.14em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#B38E4A]"
                >
                  {t.pricing.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                </button>
              </MagneticButton>
              <p className="mt-4 font-manrope text-xs text-[#5C636A]">{t.pricing.note}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
